import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Stack,
  IconButton,
  Tooltip,
  Badge,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
} from '@mui/material';
import {
  School as SchoolIcon,
  Book as BookIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Today as TodayIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Analytics as AnalyticsIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  PersonAdd as PersonAddIcon,
  Summarize as SummarizeIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { FacultySubjectService } from '../../services/facultySubjectService';
import { AttendanceService } from '../../services/attendanceService';
import { ClassService } from '../../services/classService';
import {
  FacultySubjectAssignment,
  Class,
  Student,
  AttendanceSession,
} from '../../types';

interface FacultyStats {
  totalSubjects: number;
  totalClasses: number;
  totalStudents: number;
  todaySessions: number;
  weeklyAttendanceRate: number;
}

interface AttendanceData {
  studentId: string;
  isPresent: boolean;
  remarks?: string;
}

export function FacultyDashboard() {
  const theme = useTheme();
  const { user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [assignments, setAssignments] = useState<FacultySubjectAssignment[]>([]);
  const [stats, setStats] = useState<FacultyStats>({
    totalSubjects: 0,
    totalClasses: 0,
    totalStudents: 0,
    todaySessions: 0,
    weeklyAttendanceRate: 0,
  });
  const [recentSessions, setRecentSessions] = useState<AttendanceSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);

  // Attendance Dialog State
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<FacultySubjectAssignment | null>(null);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [attendanceData, setAttendanceData] = useState<AttendanceData[]>([]);
  const [sessionType, setSessionType] = useState<'lecture' | 'practical' | 'tutorial' | 'exam'>('lecture');
  const [markingAttendance, setMarkingAttendance] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  useEffect(() => {
    setMounted(true);
    if (user?.id) {
      loadFacultyData();
    }
  }, [user]);

  const loadFacultyData = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      // Load faculty assignments
      const facultyAssignments = await FacultySubjectService.getAssignmentsByFaculty(user.id);
      setAssignments(facultyAssignments);

      // Load recent sessions
      const sessions = await AttendanceService.getAttendanceSessions({
        facultyId: user.id,
        limit: 10,
      });
      setRecentSessions(sessions);

      // Calculate stats
      await calculateStats(facultyAssignments, sessions);
    } catch (error) {
      console.error('Error loading faculty data:', error);
      showSnackbar('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = async (
    facultyAssignments: FacultySubjectAssignment[],
    sessions: AttendanceSession[]
  ) => {
    try {
      const totalSubjects = new Set(facultyAssignments.map((a) => a.subjectId)).size;
      const totalClasses = new Set(facultyAssignments.flatMap((a) => a.classIds)).size;

      // Get total students across all classes
      let totalStudents = 0;
      const classIds = new Set(facultyAssignments.flatMap((a) => a.classIds));
      
      for (const classId of Array.from(classIds)) {
        try {
          const classResult = await ClassService.getClasses(100);
          const classData = classResult.classes.find(c => c.id === classId);
          if (classData) {
            totalStudents += classData.students.length;
          }
        } catch (error) {
          console.error(`Error fetching class ${classId}:`, error);
        }
      }

      // Calculate today's sessions
      const today = new Date();
      const todaySessions = sessions.filter((session) => {
        const sessionDate = new Date(session.date);
        return sessionDate.toDateString() === today.toDateString();
      }).length;

      // Calculate weekly attendance rate
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      
      const weeklySessions = sessions.filter((session) => {
        const sessionDate = new Date(session.date);
        return sessionDate >= oneWeekAgo;
      });

      const weeklyAttendanceRate = weeklySessions.length > 0
        ? weeklySessions.reduce((sum, session) => sum + session.attendanceRate, 0) / weeklySessions.length
        : 0;

      setStats({
        totalSubjects,
        totalClasses,
        totalStudents,
        todaySessions,
        weeklyAttendanceRate: Math.round(weeklyAttendanceRate * 100) / 100,
      });
    } catch (error) {
      console.error('Error calculating stats:', error);
    }
  };

  const handleMarkAttendance = async (assignment: FacultySubjectAssignment, classId: string) => {
    try {
      // Get class details
      const classResult = await ClassService.getClasses(100);
      const classData = classResult.classes.find(c => c.id === classId);
      if (!classData) {
        throw new Error('Failed to load class details');
      }

      // Get subject details from the assignment
      const subjectDetails = assignment.classIds.includes(classId) 
        ? { id: assignment.subjectId, name: assignment.subjectName || 'Unknown Subject' }
        : null;
      
      if (!subjectDetails) {
        throw new Error('Subject not found for this class');
      }
      
      // Initialize attendance data with all students marked as absent
      const initialAttendanceData = classData.students.map((student: Student) => ({
        studentId: student.id,
        isPresent: false,
        remarks: '',
      }));
      
      setSelectedAssignment(assignment);
      setSelectedClass(classData);
      
      setAttendanceData(initialAttendanceData);
      setAttendanceDialogOpen(true);
    } catch (error) {
      console.error('Error preparing attendance:', error);
      showSnackbar('Failed to prepare attendance marking', 'error');
    }
  };

  const handleAttendanceChange = (studentId: string, isPresent: boolean) => {
    setAttendanceData((prev) =>
      prev.map((data) =>
        data.studentId === studentId ? { ...data, isPresent } : data
      )
    );
  };

  const handleRemarksChange = (studentId: string, remarks: string) => {
    setAttendanceData((prev) =>
      prev.map((data) =>
        data.studentId === studentId ? { ...data, remarks } : data
      )
    );
  };

  const submitAttendance = async () => {
    if (!selectedAssignment || !selectedClass || !user?.id) return;

    try {
      setMarkingAttendance(true);

      // Create attendance session
      const sessionId = await AttendanceService.createAttendanceSession(
        selectedClass.id,
        selectedAssignment.subjectId,
        user.id,
        new Date(),
        sessionType
      );

      // Mark attendance
      await AttendanceService.markAttendance(sessionId, attendanceData);

      showSnackbar('Attendance marked successfully', 'success');
      setAttendanceDialogOpen(false);
      
      // Reload data
      loadFacultyData();
    } catch (error) {
      console.error('Error marking attendance:', error);
      showSnackbar('Failed to mark attendance', 'error');
    } finally {
      setMarkingAttendance(false);
    }
  };

  const markAllPresent = () => {
    setAttendanceData((prev) =>
      prev.map((data) => ({ ...data, isPresent: true }))
    );
  };

  const markAllAbsent = () => {
    setAttendanceData((prev) =>
      prev.map((data) => ({ ...data, isPresent: false }))
    );
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading && !mounted) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Fade in={mounted} timeout={800}>
      <Box>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'stretch' : 'center', 
          mb: 3,
          gap: isMobile ? 2 : 0
        }}>
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700 }}>
              Faculty Dashboard
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              Welcome back, {user?.firstName && user?.lastName 
                ? `${user.firstName} ${user.lastName}` 
                : user?.username}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadFacultyData}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Box>

        {/* Faculty Statistics */}
        <Slide direction="up" in={mounted} timeout={1000}>
          <Box sx={{ mb: 4 }}>
            <Stack 
              direction={isMobile ? "column" : "row"} 
              spacing={3}
              sx={{ 
                justifyContent: "space-between",
                flexWrap: isMobile ? "nowrap" : "wrap"
              }}
            >
              <Box sx={{ flex: isMobile ? "none" : "1 1 23%" }}>
                <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <BookIcon color="primary" sx={{ fontSize: 40 }} />
                      <Box>
                        <Typography color="textSecondary" variant="body2">
                          My Subjects
                        </Typography>
                        <Typography variant="h4" color="primary">
                          {stats.totalSubjects}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: isMobile ? "none" : "1 1 23%" }}>
                <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <SchoolIcon color="success" sx={{ fontSize: 40 }} />
                      <Box>
                        <Typography color="textSecondary" variant="body2">
                          Total Classes
                        </Typography>
                        <Typography variant="h4" color="success.main">
                          {stats.totalClasses}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: isMobile ? "none" : "1 1 23%" }}>
                <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <PeopleIcon color="info" sx={{ fontSize: 40 }} />
                      <Box>
                        <Typography color="textSecondary" variant="body2">
                          Total Students
                        </Typography>
                        <Typography variant="h4" color="info.main">
                          {stats.totalStudents}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: isMobile ? "none" : "1 1 23%" }}>
                <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <AnalyticsIcon color="warning" sx={{ fontSize: 40 }} />
                      <Box>
                        <Typography color="textSecondary" variant="body2">
                          Weekly Attendance
                        </Typography>
                        <Typography variant="h4" color="warning.main">
                          {stats.weeklyAttendanceRate}%
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Stack>
          </Box>
        </Slide>

        {/* Main Content */}
        <Stack 
          direction={isMobile ? "column" : "row"}
          spacing={3}
          sx={{ alignItems: "flex-start" }}
        >
          {/* My Assignments */}
          <Box sx={{ flex: isMobile ? "none" : "1 1 65%" }}>
            <Slide direction="up" in={mounted} timeout={1200}>
              <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  My Subject Assignments
                </Typography>
                
                {assignments.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <AssignmentIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
                      No Assignments Yet
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      You don't have any subject assignments. Contact the administrator.
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0 }}>
                    {assignments.map((assignment, index) => (
                      <React.Fragment key={assignment.id}>
                        <ListItem
                          sx={{
                            bgcolor: 'grey.50',
                            borderRadius: 2,
                            mb: 2,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            animation: `fadeIn 0.6s ease-in-out ${index * 0.1}s both`,
                            '@keyframes fadeIn': {
                              from: { opacity: 0, transform: 'translateY(20px)' },
                              to: { opacity: 1, transform: 'translateY(0)' },
                            },
                          }}
                        >
                          <ListItemIcon>
                            <BookIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {assignment.subjectCode} - {assignment.subjectName}
                                </Typography>
                                <Chip
                                  label={`Sem ${assignment.semester}`}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                />
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                  Classes: {assignment.classNames?.join(', ')}
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                  {assignment.classIds.map((classId, idx) => (
                                    <Button
                                      key={classId}
                                      size="small"
                                      variant="contained"
                                      startIcon={<PersonAddIcon />}
                                      onClick={() => handleMarkAttendance(assignment, classId)}
                                      sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 500,
                                      }}
                                    >
                                      Mark Attendance - {assignment.classNames?.[idx]}
                                    </Button>
                                  ))}
                                </Stack>
                              </Box>
                            }
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Paper>
            </Slide>
          </Box>

          {/* Recent Sessions */}
          <Box sx={{ flex: isMobile ? "none" : "1 1 35%" }}>
            <Slide direction="up" in={mounted} timeout={1400}>
              <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Recent Sessions
                </Typography>
                
                {recentSessions.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <ScheduleIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                    <Typography variant="body2" color="textSecondary">
                      No recent sessions
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0 }}>
                    {recentSessions.slice(0, 5).map((session) => (
                      <ListItem key={session.id} sx={{ px: 0, py: 1 }}>
                        <ListItemIcon>
                          <CalendarIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {session.subjectCode} - {session.className}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <Typography variant="caption" color="textSecondary">
                                {new Date(session.date).toLocaleDateString()}
                              </Typography>
                              <Chip
                                label={`${session.attendanceRate}%`}
                                size="small"
                                color={session.attendanceRate >= 75 ? 'success' : 'warning'}
                                sx={{ fontSize: '0.7rem' }}
                              />
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </Slide>
          </Box>
        </Stack>

        {/* Attendance Marking Dialog */}
        <Dialog
          open={attendanceDialogOpen}
          onClose={() => setAttendanceDialogOpen(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallScreen}
          PaperProps={{
            sx: {
              borderRadius: isSmallScreen ? 0 : 3,
              margin: isSmallScreen ? 0 : 2
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Mark Attendance
                </Typography>
                <Typography variant="subtitle2" color="textSecondary">
                  {selectedAssignment?.subjectCode} - {selectedClass?.name}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  onClick={markAllPresent}
                  startIcon={<CheckCircleIcon />}
                >
                  All Present
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={markAllAbsent}
                  startIcon={<CancelIcon />}
                >
                  All Absent
                </Button>
              </Box>
            </Box>
          </DialogTitle>
          
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={3}>
              <FormControl size="small">
                <InputLabel>Session Type</InputLabel>
                <Select
                  value={sessionType}
                  onChange={(e) => setSessionType(e.target.value as any)}
                  label="Session Type"
                >
                  <MenuItem value="lecture">Lecture</MenuItem>
                  <MenuItem value="practical">Practical</MenuItem>
                  <MenuItem value="tutorial">Tutorial</MenuItem>
                  <MenuItem value="exam">Exam</MenuItem>
                </Select>
              </FormControl>

              <Divider />

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Students ({selectedClass?.students.length})
                </Typography>
                
                <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {selectedClass?.students.map((student) => {
                    const attendanceRecord = attendanceData.find(d => d.studentId === student.id);
                    return (
                      <ListItem key={student.id} sx={{ px: 0 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={attendanceRecord?.isPresent || false}
                              onChange={(e) => handleAttendanceChange(student.id, e.target.checked)}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {student.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                Roll: {student.rollNumber}
                              </Typography>
                            </Box>
                          }
                          sx={{ flex: 1 }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            </Stack>
          </DialogContent>
          
          <DialogActions sx={{ p: 3, pt: 1, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
            <Button
              onClick={() => setAttendanceDialogOpen(false)}
              sx={{ textTransform: 'none', width: isMobile ? '100%' : 'auto' }}
              variant={isMobile ? 'outlined' : 'text'}
            >
              Cancel
            </Button>
            <Button
              onClick={submitAttendance}
              variant="contained"
              disabled={markingAttendance}
              startIcon={markingAttendance ? <CircularProgress size={20} /> : <CheckCircleIcon />}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 600,
                width: isMobile ? '100%' : 'auto'
              }}
            >
              {markingAttendance ? 'Marking...' : 'Mark Attendance'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%', borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
} 